<template>
  <div class="p-4">
    <div class="flex flex-wrap items-center gap-2 mb-4">
      <button
        v-for="f in findingChips"
        :key="f.key"
        class="px-3 py-1.5 rounded border text-xs"
        :class="activeFinding === f.key
          ? 'bg-blue-600 text-white border-blue-600'
          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'"
        @click="activeFinding = f.key"
      >
        {{ f.label }}
        <span class="ml-1 opacity-70">{{ f.count }}</span>
      </button>

      <div class="flex-1"></div>

      <input
        v-model="search" data-demo-search
        type="text"
        placeholder="Filter by name, NPI, TIN, or practice"
        class="sensitive border border-gray-300 rounded px-3 py-1.5 text-xs w-64"
      />
      <button
        class="px-3 py-1.5 border border-gray-300 rounded text-xs hover:bg-gray-50"
        :disabled="!filteredRows.length"
        @click="exportCsv"
      >Export CSV</button>
    </div>

    <LoadingIndicator v-if="loading" />

    <div v-else-if="error" class="text-red-600 text-xs p-4 border border-red-200 rounded bg-red-50">
      {{ error }}
    </div>

    <div v-else class="border border-gray-200 rounded max-h-[70vh] overflow-y-auto">
      <table class="w-full table-fixed text-xs">
        <colgroup>
          <col style="width:14%" />
          <col style="width:15%" />
          <col style="width:9%" />
          <col style="width:9%" />
          <col style="width:8%" />
          <col style="width:7%" />
          <col style="width:8%" />
          <col style="width:16%" />
          <col style="width:14%" />
        </colgroup>
        <thead class="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
          <tr>
            <th class="text-left px-2 py-1.5 font-medium">Staff</th>
            <th class="text-left px-2 py-1.5 font-medium">Practice</th>
            <th class="text-left px-2 py-1.5 font-medium">NPI</th>
            <th class="text-left px-2 py-1.5 font-medium">Linked TIN</th>
            <th class="text-right px-2 py-1.5 font-medium">Claims 3yr</th>
            <th class="text-right px-2 py-1.5 font-medium">Last asgn</th>
            <th class="text-right px-2 py-1.5 font-medium">Pts elsewhere</th>
            <th class="text-left px-2 py-1.5 font-medium">Actually assigned at</th>
            <th class="text-left px-2 py-1.5 font-medium">Finding</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(r, idx) in filteredRows" :key="idx" class="border-b border-gray-100 hover:bg-gray-50">
            <td class="sensitive px-2 py-1.5 truncate" :title="r.STAFF_NAME">
              <router-link
                v-if="r.STAFF_ENTITY_ID"
                :to="`/entity/${r.STAFF_ENTITY_ID}`"
                class="text-blue-600 hover:underline"
              >{{ r.STAFF_NAME }}</router-link>
              <span v-else>{{ r.STAFF_NAME }}</span>
            </td>
            <td class="sensitive px-2 py-1.5 truncate text-gray-600" :title="r.PRACTICE_NAME">
              {{ r.PRACTICE_NAME || '—' }}
            </td>
            <td class="px-2 py-1.5 truncate sensitive">{{ r.NPI }}</td>
            <td class="px-2 py-1.5 truncate sensitive">{{ r.TIN }}</td>
            <td class="px-2 py-1.5 text-right">{{ fmt(r.CLAIMS_3YR) }}</td>
            <td class="px-2 py-1.5 text-right text-gray-600">{{ r.LAST_ASSIGNED_YEAR || '—' }}</td>
            <td class="px-2 py-1.5 text-right font-medium">{{ fmt(r.PATIENTS_AT_OTHER_TINS) }}</td>
            <td class="px-2 py-1.5 truncate text-gray-600" :title="r.NPI_ASSIGNED_AT_TINS">
              {{ r.NPI_ASSIGNED_AT_TINS || '—' }}
            </td>
            <td class="px-2 py-1.5 truncate" :title="label(r.FINDING)">
              <span class="px-1.5 py-0.5 rounded" :class="badgeClass(r.FINDING)">
                {{ shortLabel(r.FINDING) }}
              </span>
            </td>
          </tr>
          <tr v-if="!filteredRows.length">
            <td colspan="9" class="px-3 py-8 text-center text-gray-500">
              No rows match the current filter.
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="!loading" class="mt-3 text-xs text-gray-600">
      {{ fmt(filteredRows.length) }} rows
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import { blockSensitiveEgress } from '@/utils/demoEgress';
import demoProtectedFields from '@/mixins/demoProtectedFields';

const FINDING_LABELS = {
  ALL: 'All',
  PROBLEMS: 'Problems only',
  NO_BASIS_ASSIGNED_ELSEWHERE: 'No basis — assigned at another TIN',
  NO_BASIS_ANYWHERE: 'No basis anywhere',
  PRIOR_YEAR_ASSIGNMENT_ONLY: 'Prior-year assignment only',
  CLAIMS_ONLY_NO_ASSIGNMENT: 'Claims only, no assignment',
  OK: 'OK',
};

const SHORT_LABELS = {
  NO_BASIS_ASSIGNED_ELSEWHERE: 'Wrong TIN',
  NO_BASIS_ANYWHERE: 'No basis',
  PRIOR_YEAR_ASSIGNMENT_ONLY: 'Prior year',
  CLAIMS_ONLY_NO_ASSIGNMENT: 'Claims only',
  OK: 'OK',
};

export default {
  name: 'ReconciliationUnsupported',
  components: { LoadingIndicator },
  mixins: [demoProtectedFields],
  // The name/NPI/TIN/practice filter identifies real providers and practices. Demo Mode empties it and the tagged
  // input is read-only while protected. It is not persisted.
  demoProtectedFields: ['search'],
  data() {
    return {
      loading: true,
      error: null,
      rows: [],
      summary: {},
      activeFinding: 'PROBLEMS',
      search: '',
    };
  },
  computed: {
    findingChips() {
      const total = this.rows.length;
      const problems = this.rows.filter((r) => r.FINDING !== 'OK').length;
      const chips = [
        { key: 'ALL', label: FINDING_LABELS.ALL, count: total },
        { key: 'PROBLEMS', label: FINDING_LABELS.PROBLEMS, count: problems },
      ];
      Object.keys(this.summary)
        .sort((a, b) => this.summary[b] - this.summary[a])
        .forEach((k) => chips.push({
          key: k, label: FINDING_LABELS[k] || k, count: this.summary[k],
        }));
      return chips;
    },
    filteredRows() {
      let out = this.rows;
      if (this.activeFinding === 'PROBLEMS') {
        out = out.filter((r) => r.FINDING !== 'OK');
      } else if (this.activeFinding !== 'ALL') {
        out = out.filter((r) => r.FINDING === this.activeFinding);
      }
      const q = this.search.trim().toLowerCase();
      if (q) {
        out = out.filter((r) => (
          (r.STAFF_NAME || '').toLowerCase().includes(q)
          || (r.PRACTICE_NAME || '').toLowerCase().includes(q)
          || (r.NPI || '').includes(q)
          || (r.TIN || '').includes(q)
        ));
      }
      return out;
    },
  },
  mounted() { this.fetch(); },
  methods: {
    async fetch() {
      this.loading = true;
      this.error = null;
      try {
        const res = await axios.get('/api/entities/unsupported-links/');
        this.rows = res.data.rows || [];
        this.summary = res.data.summary || {};
      } catch (e) {
        this.error = e.response?.data?.detail || 'Could not load unsupported links.';
      } finally {
        this.loading = false;
      }
    },
    label(f) { return FINDING_LABELS[f] || f; },
    shortLabel(f) { return SHORT_LABELS[f] || FINDING_LABELS[f] || f; },
    fmt(n) {
      if (n === null || n === undefined) return '—';
      return Number(n).toLocaleString();
    },
    badgeClass(f) {
      switch (f) {
        case 'OK': return 'bg-green-100 text-green-800';
        case 'CLAIMS_ONLY_NO_ASSIGNMENT': return 'bg-gray-100 text-gray-700';
        case 'PRIOR_YEAR_ASSIGNMENT_ONLY': return 'bg-amber-100 text-amber-800';
        default: return 'bg-red-100 text-red-800';
      }
    },
    exportCsv() {
      if (blockSensitiveEgress('export')) return;
      const cols = ['STAFF_ENTITY_ID', 'STAFF_NAME', 'PRACTICE_NAME', 'NPI', 'TIN',
        'CLAIMS_3YR', 'LAST_ASSIGNED_YEAR', 'PATIENTS_AT_OTHER_TINS',
        'NPI_ASSIGNED_AT_TINS', 'FINDING'];
      const esc = (v) => {
        if (v === null || v === undefined) return '';
        const s = String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const csv = [cols.join(',')]
        .concat(this.filteredRows.map((r) => cols.map((c) => esc(r[c])).join(',')))
        .join('\n');
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'entity_unsupported_links.csv';
      a.click();
      URL.revokeObjectURL(url);
    },
  },
};
</script>
